<template>
  <div class="blog-content flex flex-col gap-8">
    <template v-for="(block, index) in blocks" :key="index">
      <!-- Текст и таблица приходят готовым html: бэк уже санитизировал его
           по белому списку и проставил якоря заголовков, чистить повторно
           не нужно (см. blog-frontend-contract.md, раздел 5) -->
      <div v-if="block.type === 'text'" class="blog-html" v-html="block.html"></div>

      <figure v-else-if="block.type === 'image' && block.url" class="flex flex-col gap-3">
        <img :src="block.url" :alt="block.alt || ''" class="w-full rounded-2xl bg-[#E5EBF5]" />
        <figcaption v-if="block.caption" class="text-sm text-txmain/80">
          {{ block.caption }}
        </figcaption>
      </figure>

      <figure v-else-if="block.type === 'video' && block.url" class="flex flex-col gap-3">
        <video
          :src="block.url"
          :poster="block.poster || undefined"
          controls
          class="w-full rounded-2xl bg-[#3A3D44]"
        ></video>
        <figcaption v-if="block.caption" class="text-sm text-txmain/80">
          {{ block.caption }}
        </figcaption>
      </figure>

      <blockquote
        v-else-if="block.type === 'quote'"
        class="flex gap-4 overflow-hidden rounded-2xl bg-[#E9F0FF] p-6"
      >
        <span class="w-1 shrink-0 self-stretch bg-[#2C6BFF]" />
        <div class="min-w-0 flex-1">
          <p class="text-lg font-medium text-[#2C6BFF]">{{ block.text }}</p>
          <footer v-if="block.author" class="mt-2 text-sm text-[#2C6BFF]/70">
            — {{ block.author }}
          </footer>
        </div>
      </blockquote>

      <aside
        v-else-if="block.type === 'callout'"
        class="rounded-2xl border border-[#E5EBF5] bg-[#EEF3FD] p-6"
      >
        <p v-if="block.title" class="mb-2 text-base font-bold text-txmain">{{ block.title }}</p>
        <p class="text-base leading-[1.6] text-txmain">{{ block.text }}</p>
      </aside>

      <div
        v-else-if="block.type === 'cta'"
        class="flex flex-col items-start gap-3 rounded-2xl border border-[#E5EBF5] bg-[#EEF3FD] p-6"
      >
        <p class="text-lg font-bold text-txmain">{{ block.title }}</p>
        <p v-if="block.text" class="text-base leading-[1.6] text-txmain">{{ block.text }}</p>
        <a v-if="block.url" :href="block.url" target="_blank" rel="noopener" class="blog-btn">
          {{ block.btnText }}
        </a>
      </div>

      <a
        v-else-if="block.type === 'button' && block.url"
        :href="block.url"
        target="_blank"
        rel="noopener"
        class="blog-btn"
      >
        {{ block.text }}
      </a>

      <a
        v-else-if="block.type === 'document' && block.url"
        :href="block.url"
        target="_blank"
        rel="noopener"
        class="flex w-fit items-center gap-2 rounded-lg border border-[#2C6BFF]/15 bg-white px-4 py-2.5 text-sm font-semibold text-[#2C6BFF] transition-colors hover:bg-[#EEF3FD]"
      >
        <Icon name="lucide:download" size="16" class="block text-[#2C6BFF]" />
        {{ block.fileName || "Скачать документ" }}
        <span v-if="block.fileSize" class="font-normal text-txopacity">
          {{ formatSize(block.fileSize) }}
        </span>
      </a>

      <figure v-else-if="block.type === 'table'" class="flex flex-col gap-3">
        <div class="blog-table overflow-x-auto rounded-2xl border border-[#E5EBF5] bg-white" v-html="block.html"></div>
        <figcaption v-if="block.caption" class="text-sm text-txmain/80">
          {{ block.caption }}
        </figcaption>
      </figure>

      <pre
        v-else-if="block.type === 'code'"
        class="overflow-x-auto rounded-2xl bg-[#3A3D44] p-4 font-mono text-[13px] leading-[1.6] text-[#E5EBF5]"
      ><code>{{ block.code }}</code></pre>

      <!-- Неизвестный тип пропускаем молча: набор блоков на бэке расширяется -->
    </template>
  </div>
</template>

<script setup lang="ts">
  import type { BlogBlock } from "~/interfaces/blog";

  defineProps<{ blocks: BlogBlock[] }>();

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  };
</script>

<style lang="scss" scoped>
  .blog-btn {
    @apply w-fit rounded-lg bg-[#2C6BFF] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1E58E0];
  }

  // ===== Тело статьи: html-блоки =====
  // Базовый кегль 16 задаём на контейнере: иначе голый текст и ссылки вне
  // абзаца наследуют 14px со страницы
  .blog-html {
    @apply flex flex-col gap-4 text-base leading-[1.6] text-txmain;
    // Длинные ссылки и слова не должны выпирать за колонку
    overflow-wrap: anywhere;
  }

  // Шапка сервиса зафиксирована сверху: без запаса переход по якорю из
  // оглавления прячет заголовок под ней
  .blog-html :deep(h1),
  .blog-html :deep(h2),
  .blog-html :deep(h3),
  .blog-html :deep(h4),
  .blog-html :deep(h5),
  .blog-html :deep(h6) {
    scroll-margin-top: 96px;

    @media (max-width: 767px) {
      scroll-margin-top: 64px;
    }
  }

  // Заголовок должен отделяться от предыдущего текста заметнее, чем абзацы
  // друг от друга. Добавка к общему gap-4 колонки; у первого блока лишний
  // отступ сверху не нужен
  .blog-html :deep(h1):not(:first-child),
  .blog-html :deep(h2):not(:first-child),
  .blog-html :deep(h3):not(:first-child),
  .blog-html :deep(h4):not(:first-child),
  .blog-html :deep(h5):not(:first-child),
  .blog-html :deep(h6):not(:first-child) {
    margin-top: 1.5rem;
  }

  .blog-html :deep(h1) {
    @apply text-[36px] font-bold leading-[1.2] text-txmain max-md:text-[32px];
  }
  .blog-html :deep(h2) {
    @apply text-2xl font-bold leading-[1.2] text-txmain;
  }
  .blog-html :deep(h3) {
    @apply text-xl font-bold leading-[1.2] text-txmain;
  }
  .blog-html :deep(h4) {
    @apply text-lg font-bold leading-[1.2] text-txmain;
  }
  .blog-html :deep(h5) {
    @apply text-base font-bold leading-[1.2] text-txmain;
  }
  .blog-html :deep(h6) {
    @apply text-sm font-bold leading-[1.2] text-txmain;
  }

  .blog-html :deep(p) {
    @apply text-base leading-[1.6] text-txmain;
  }

  .blog-html :deep(strong),
  .blog-html :deep(b) {
    @apply font-bold;
  }
  .blog-html :deep(em),
  .blog-html :deep(i) {
    @apply italic;
  }
  .blog-html :deep(u) {
    @apply underline;
  }

  .blog-html :deep(a) {
    @apply text-[#2C6BFF] underline;

    &:hover {
      @apply text-[#003DCD];
    }
  }

  // Маркеры списков рисуем сами: в макете точка серая, а номер синий и жирный.
  // Сам список сдвигаем от колонки текста — так он читается как вложенный,
  // а не как продолжение абзаца
  .blog-html :deep(ul),
  .blog-html :deep(ol) {
    @apply flex list-none flex-col gap-2 py-0 pl-6 pr-0;
  }

  .blog-html :deep(li) {
    @apply relative text-base leading-[1.5] text-txmain;
  }

  // Отступ под маркер = его ширина + 12 (gap в макете): у точки это 8, у номера 15
  .blog-html :deep(ul > li) {
    @apply pl-5;
  }

  .blog-html :deep(ol > li) {
    @apply pl-7;
  }

  .blog-html :deep(ul > li)::before {
    content: "•";
    @apply absolute left-0 text-txmain;
  }

  .blog-html :deep(ol) {
    counter-reset: blog-ol;
  }

  .blog-html :deep(ol > li) {
    counter-increment: blog-ol;
  }

  .blog-html :deep(ol > li)::before {
    content: counter(blog-ol) ".";
    @apply absolute left-0 font-bold text-[#2C6BFF];
  }

  // Синяя полоска в макете лежит внутри отступов (p-24 + полоска 4 + gap 16),
  // а не приклеена к краю блока, поэтому рисуем её псевдоэлементом
  .blog-html :deep(blockquote) {
    @apply relative rounded-2xl bg-[#E9F0FF] py-6 pl-11 pr-6 text-lg font-medium text-[#2C6BFF];

    &::before {
      content: "";
      @apply absolute bottom-6 left-6 top-6 w-1 bg-[#2C6BFF];
    }
  }

  .blog-html :deep(img) {
    @apply w-full rounded-2xl;
  }

  .blog-html :deep(pre) {
    @apply overflow-x-auto rounded-2xl bg-[#3A3D44] p-4 font-mono text-[13px] leading-[1.6] text-[#E5EBF5];
  }

  // ===== Таблицы (и отдельным блоком, и внутри html) =====
  .blog-table :deep(table),
  .blog-html :deep(table) {
    @apply w-full border-collapse text-sm;
  }

  .blog-table :deep(th),
  .blog-html :deep(th) {
    @apply bg-[#2C6BFF] px-4 py-4 text-left font-bold text-white;
  }

  .blog-table :deep(td),
  .blog-html :deep(td) {
    @apply px-4 py-4 align-middle text-txmain;
  }

  .blog-table :deep(tbody tr:nth-child(even)),
  .blog-html :deep(tbody tr:nth-child(even)) {
    @apply bg-[#F5F7FA];
  }

  // Содержимое ячейки редактор заворачивает в <p>, а у абзацев в теле статьи
  // свои цвет и размер — в таблице они должны наследоваться от ячейки,
  // иначе текст шапки остаётся тёмным на синем
  .blog-table :deep(th p),
  .blog-table :deep(td p),
  .blog-html :deep(th p),
  .blog-html :deep(td p) {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    margin: 0;
  }

  .blog-html :deep(table) {
    @apply overflow-hidden rounded-2xl border border-[#E5EBF5] bg-white;
  }

  // На узком экране широкая таблица прокручивается сама, а не растягивает страницу
  @media (max-width: 640px) {
    .blog-html :deep(table) {
      display: block;
      overflow-x: auto;
    }
  }
</style>
